/**
 * =============================================================================
 * LA SERIE DE PROGRESION — Y LA LINEA QUE NO SE DEBE PINTAR
 * =============================================================================
 * Aqui esta el riesgo mayor de toda la capa de lectura, y no se ve mirando la
 * pantalla:
 *
 * **Una linea cuya primera mitad significa "errores" y la segunda "puntuacion"
 * parece una linea normal.** El terapeuta la lee como una evolucion y saca una
 * conclusion clinica de una grafica que mezcla dos cosas distintas.
 *
 * Eso pasa en cuanto un paciente tiene sesiones de antes y de despues del
 * contrato v3, que es exactamente lo que va a pasar con todos: las experiencias
 * actuales se retiran y entran las nuevas.
 *
 * Asi que esta funcion tiene tres salidas, no dos:
 *
 *   - `core`   — todas las sesiones traen puntuacion normalizada. Se pinta, y
 *                es comparable incluso entre experiencias distintas.
 *   - `legacy` — ninguna la trae. Se pinta lo de siempre, adivinando.
 *   - `mixta`  — unas si y otras no. **NO se pinta**: se dice.
 *
 * La tercera es la que importa. Callarse y pintar la linea igual seria el tipo
 * de error que nadie detecta hasta que alguien toma una decision clinica con
 * ella.
 * =============================================================================
 */

/** Un valor que se puede pintar como numero. */
function esNumerico(v) {
  if (typeof v === "number" && Number.isFinite(v)) return true;
  if (typeof v === "string" && v !== "" && !Number.isNaN(Number(v)))
    return true;
  return false;
}

/** La puntuacion normalizada de una sesion, o null si no la tiene. */
function puntuacionDe(sesion) {
  const score = sesion?.derived?.core?.score;
  return typeof score === "number" && Number.isFinite(score) ? score : null;
}

/**
 * Decide que se puede pintar con este conjunto de sesiones.
 *
 * @param {Array} sesiones  ya ordenadas cronologicamente
 * @param {object} [game]   entrada del catalogo, solo para el camino legacy
 * @returns {{modo: "core"|"legacy"|"mixta", claveLegacy: string|null,
 *            conNucleo: number, total: number}}
 */
export function modoDeProgresion(sesiones, game) {
  const lista = Array.isArray(sesiones) ? sesiones : [];
  if (lista.length === 0) {
    return { modo: "legacy", claveLegacy: null, conNucleo: 0, total: 0 };
  }

  const conNucleo = lista.filter((s) => puntuacionDe(s) !== null).length;

  if (conNucleo === lista.length) {
    return {
      modo: "core",
      claveLegacy: null,
      conNucleo,
      total: lista.length,
    };
  }

  if (conNucleo > 0) {
    // Ni una linea ni la otra: mezclarlas cambiaria el significado a mitad del
    // eje. Se dice y ya esta.
    return { modo: "mixta", claveLegacy: null, conNucleo, total: lista.length };
  }

  // Ninguna trae nucleo: camino de siempre.
  const summaries = lista.map((s) => s?.summary || {});
  let claveLegacy = null;
  if (summaries.every((s) => esNumerico(s.totalFaults))) {
    claveLegacy = "totalFaults";
  } else if (
    game?.primaryMetric &&
    summaries.every((s) => esNumerico(s[game.primaryMetric]))
  ) {
    claveLegacy = game.primaryMetric;
  }

  return { modo: "legacy", claveLegacy, conNucleo: 0, total: lista.length };
}

/**
 * Los puntos de la serie principal.
 *
 * @param {Array} sesiones  ya ordenadas
 * @param {object} modo     lo que devuelve `modoDeProgresion`
 * @param {Function} fecha  formateador de la etiqueta del eje
 */
export function puntosDeProgresion(sesiones, modo, fecha) {
  const lista = Array.isArray(sesiones) ? sesiones : [];

  if (modo.modo === "core") {
    return lista.map((s) => ({
      date: fecha(s.startedAt || s.createdAt),
      value: puntuacionDe(s),
    }));
  }

  if (modo.modo === "legacy" && modo.claveLegacy) {
    return lista.map((s) => ({
      date: fecha(s.startedAt || s.createdAt),
      value: Number(s?.summary?.[modo.claveLegacy]) || 0,
    }));
  }

  return [];
}
